/**
 * TODO: merge this with get_async_data_component_and_dependencies
 * But this is also stateful to optimise (prematurely?) for rendering React
 * components.  So either remove this optimisation and merge with
 * get_async_data_component_and_dependencies, or rename this from
 * "accessor" to something else which is not synonymous with pure (stateless) functions.
 */
import { useMemo } from "preact/hooks"

import { IdAndVersion, IdOnly, OrderedUniqueIdAndVersionList } from "core/data/id"
import { AsyncDataComponent, BasicLoadingStatus, DataComponent, NewDataComponent } from "core/data/interface"
import {
    browser_get_referenced_ids,
} from "core/rich_text/browser_get_referenced_ids"

import { RootAppState } from "../interface"


interface AsyncLoadReferencedDataComponentsResult
{
    status: BasicLoadingStatus
    error: string | undefined
    loading_count: number
    referenced_data_component_ids: IdAndVersion[]
    referenced_data_components_by_id_str: Record<string, DataComponent>
}
export function load_referenced_data_components(state: RootAppState, data_component: DataComponent | NewDataComponent): AsyncLoadReferencedDataComponentsResult
{
    let error: string | undefined = undefined

    const partial_referenced_data_component_ids = useMemo(() =>
    {
        let ids: IdAndVersion[] = []
        try
        {
            ids = browser_get_referenced_ids(data_component)
        }
        catch (e)
        {
            error = (e as Error).message
        }

        return ids
    }, [data_component.input_value])


    // Request the direct (parent) dependencies first, this will be a partial
    // list of all the dependencies needed
    const async_partial_referenced_data_components = useMemo(() =>
    {
        // console .log("Requesting partial referenced data components", partial_referenced_data_component_ids.map(id => id.to_str()).join(", "))
        return state.data_components.request_data_components(partial_referenced_data_component_ids)
    }, [partial_referenced_data_component_ids, state.data_components.data_component_by_id_and_maybe_version])

    const result1 = process_async_data_components(async_partial_referenced_data_components, error)
    const { components_by_id_str: partial_referenced_data_components_by_id_str } = result1
    error = result1.error


    const ids_set = new OrderedUniqueIdAndVersionList()
    ids_set.add_multiple(partial_referenced_data_component_ids)
    // Load the grandparent components too, this will complete the recursion
    // to its full depth
    const grand_parent_ids: IdAndVersion[] = Object.values(partial_referenced_data_components_by_id_str)
        .map(c => c.recursive_dependency_ids || []).flat()
    ids_set.add_multiple(grand_parent_ids)

    const all_referenced_data_component_ids = ids_set.get_all()
    const async_all_referenced_data_components = useMemo(() =>
    {
        // console .log("Requesting all referenced data components", all_referenced_data_component_ids.map(id => id.to_str()).join(", "))
        return state.data_components.request_data_components(all_referenced_data_component_ids)
    }, [ids_set.to_str(), state.data_components.data_component_by_id_and_maybe_version])


    const result2 = process_async_data_components(async_all_referenced_data_components, error)
    const { components_by_id_str: all_referenced_data_components_by_id_str } = result2
    error = result2.error


    const loading = async_all_referenced_data_components.filter(async_data_component => async_data_component.status === "loading")
    const loading_count = loading.length
    const status = error ? "error" : (loading_count > 0 ? "loading" : "loaded")

    // // Check that all the references ids are actually present in the _by_id_str object
    // all_referenced_data_component_ids.forEach(id =>
    // {
    //     if (!all_referenced_data_components_by_id_str[id.to_str()])
    //     {
    //         console .error(`Data component with id ${id.to_str()} is referenced but not present in the all_referenced_data_components_by_id_str object.`)
    //     }
    // })

    return {
        status,
        error,
        loading_count,
        referenced_data_component_ids: all_referenced_data_component_ids,
        referenced_data_components_by_id_str: all_referenced_data_components_by_id_str,
    }
}


export function load_referenced_subject_and_according_to_components(state: RootAppState, data_component: DataComponent | NewDataComponent)
{
    let error: string | undefined = undefined

    const subject_and_according_to_ids = useMemo(() =>
    {
        const ids: IdOnly[] = []
        if (data_component.subject_id)
        {
            ids.push(new IdOnly(data_component.subject_id))
        }
        if (data_component.according_to_id)
        {
            ids.push(new IdOnly(data_component.according_to_id))
        }
        return ids
    }, [data_component.subject_id, data_component.according_to_id])


    const async_subject_and_according_to_components = useMemo(() =>
    {
        return state.data_components.request_data_components(subject_and_according_to_ids)
    }, [subject_and_according_to_ids, state.data_components.data_component_by_id_and_maybe_version])


    const result = process_async_data_components(async_subject_and_according_to_components, error)
    // const { components_by_id_str: subject_and_according_to_components_by_id_str } = result
    error = result.error

    const loading = async_subject_and_according_to_components.filter(async_data_component => async_data_component.status === "loading")
    const loading_count = loading.length
    const status: BasicLoadingStatus = error ? "error" : (loading_count > 0 ? "loading" : "loaded")

    return {
        status,
        error,
        loading_count,
        subject_and_according_to_ids,
    }
}


function process_async_data_components(async_referenced_data_components: AsyncDataComponent[], error: string | undefined)
{
    const components_by_id_str: Record<string, DataComponent> = {}
    async_referenced_data_components.forEach(async_data_component => {
        const { component } = async_data_component
        if (async_data_component.status === "loaded" && component) {
            components_by_id_str[component.id.to_str()] = component
        }
        else if (async_data_component.status === "error") {
            error = typeof async_data_component.error === "string"
                ? async_data_component.error
                : async_data_component.error?.message ?? "Unknown error"
        }
    })
    return { components_by_id_str, error }
}
